import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Phone, Mail, Calendar, UserRound } from 'lucide-react'
import api from '../services/api'

export default function GenericSite() {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        // Appelle l'API Widget (qui utilise le contexte du cabinet courant)
        api.get('/api/workspaces/widget-data')
           .then(res => setData(res.data))
           .catch(console.error)
           .finally(() => setLoading(false))
    },[])

    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner border-sage-600" /></div>
    if (!data) return <div className="text-center py-20 text-gray-500">Données du cabinet introuvables.</div>

    const { profile, reasons, tariffs, landing_config, settings } = data
    
    // Déduction des couleurs (avec fallback sécurisé)
    const primaryColor = landing_config?.theme?.text_color || '#7d9668' 
    const bgColor = landing_config?.theme?.bg_color || '#fdfdf9'       

    // Placeholder pour la bio (À dynamiser plus tard via les settings)
    const bioText = landing_config?.philosophy?.subtitle || "Je vous accompagne vers une alimentation équilibrée et sereine, adaptée à votre mode de vie et à vos besoins spécifiques."

    return (
        <div className="min-h-screen pb-24 md:pb-12 font-sans text-gray-800" style={{ backgroundColor: bgColor }}>
            <div className="max-w-4xl mx-auto px-4 py-8 md:py-16 space-y-6 md:space-y-8">
                
                {/* 1. HERO & BIO (Style Carte de visite Premium) */}
                <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-sm border border-gray-100 flex flex-col md:flex-row items-center md:items-start gap-8 relative overflow-hidden">
                    {/* Décoration douce en arrière-plan */}
                    <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" style={{ backgroundColor: primaryColor + '15' }}></div>

                    {/* Avatar générique stylisé ou Photo */}
                    <div className="w-28 h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center shrink-0 z-10 shadow-inner overflow-hidden border-4 border-white" style={{ backgroundColor: primaryColor + '10', color: primaryColor }}>
                        {profile.avatar_url ? (
                            <img src={profile.avatar_url} alt={profile.admin_name} className="w-full h-full object-cover" />
                        ) : (
                            <UserRound className="w-12 h-12 md:w-16 md:h-16 opacity-80" />
                        )}
                    </div>

                    <div className="text-center md:text-left z-10 flex-1">
                        <h1 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-2">{profile.admin_name}</h1>
                        <p className="text-lg font-bold mb-5 tracking-wide" style={{ color: primaryColor }}>{profile.admin_title}</p>
                        <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl text-lg">{bioText}</p>

                        {/* Bouton RDV (Affiché ici uniquement sur Ordinateur) */}
                        <div className="hidden md:flex items-center gap-4">
                            <Link to="/booking" className="flex items-center gap-2 px-8 py-4 rounded-2xl text-white font-bold transition-transform hover:-translate-y-1 shadow-lg" style={{ backgroundColor: primaryColor, boxShadow: `0 10px 25px -5px ${primaryColor}40` }}>
                                <Calendar className="w-5 h-5" />
                                Prendre Rendez-vous
                            </Link>
                            {profile.rpps && <span className="text-xs text-gray-400 font-mono ml-4 border-l border-gray-200 pl-4">RPPS: {profile.rpps}</span>}
                        </div>
                    </div>
                </div>

                {/* 2. GRILLE BENTO (2 Colonnes sur PC) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    
                    {/* CARTE : INFORMATIONS PRATIQUES */}
                    <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 flex flex-col h-full">
                        <h2 className="text-xl font-serif font-bold text-gray-900 mb-6">Informations pratiques</h2>
                        <div className="space-y-6 flex-1">
                            {profile.cabinet_name && (
                                <div className="flex gap-4 items-start">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}><MapPin className="w-5 h-5"/></div>
                                    <div className="pt-1">
                                        <p className="font-bold text-gray-900 text-lg">{profile.cabinet_name}</p>
                                        <p className="text-gray-600 whitespace-pre-line mt-1">{profile.address}</p>
                                    </div>
                                </div>
                            )}
                            {profile.phone && (
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}><Phone className="w-5 h-5"/></div>
                                    <p className="font-bold text-gray-700 text-lg pt-1">{profile.phone}</p>
                                </div>
                            )}
                            {profile.email && (
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}><Mail className="w-5 h-5"/></div>
                                    <a href={`mailto:${profile.email}`} className="font-bold text-gray-700 text-lg hover:opacity-80 transition-opacity pt-1" style={{ textDecorationColor: primaryColor }}>
                                        {profile.email}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CARTE : TARIFS */}
                    {settings.show_tariffs && tariffs.length > 0 && (
                        <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 flex flex-col h-full">
                            <h2 className="text-xl font-serif font-bold text-gray-900 mb-6">Honoraires</h2>
                            <div className="space-y-3 flex-1">
                                {tariffs.map(t => (
                                    <div key={t.id} className="flex justify-between items-center p-4 rounded-2xl border border-gray-100 hover:bg-gray-50 transition-colors">
                                        <span className="font-bold text-gray-700">{t.name}</span>
                                        <span className="font-black text-xl" style={{ color: primaryColor }}>
                                            {t.amount.toFixed(2)} €
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-4 border-t border-gray-100 text-sm text-gray-400">
                                Une facture vous sera délivrée à l'issue de la séance pour votre mutuelle.
                            </div>
                        </div>
                    )}

                    {/* CARTE : MOTIFS (Prend toute la largeur en bas) */}
                    {reasons && reasons.length > 0 && (
                        <div className="md:col-span-2 bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100">
                            <h2 className="text-xl font-serif font-bold text-gray-900 mb-6">Motifs de consultation traités</h2>
                            <div className="flex flex-wrap gap-3">
                                {reasons.map(r => (
                                    <div key={r.id} className="px-4 py-2.5 rounded-xl text-sm font-bold border transition-colors" style={{ backgroundColor: primaryColor + '08', borderColor: primaryColor + '20', color: primaryColor }}>
                                        {r.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CTA FLOTTANT (Visible uniquement sur Mobile) */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-xl border-t border-gray-200 z-50">
                <Link 
                    to="/booking" 
                    className="w-full flex justify-center items-center gap-2 py-4 shadow-xl text-lg rounded-2xl font-bold transition-transform active:scale-95"
                    style={{ backgroundColor: primaryColor, color: '#ffffff' }}
                >
                    <Calendar className="w-6 h-6"/> Prendre Rendez-vous
                </Link>
            </div>
        </div>
    )
}